// Copyright (c) StrangeBee 2023

import { useCallback, useMemo } from 'react';
import { QueryKey, UseQueryResult } from 'react-query';
import client from '@Client';
import { useGenericQuery } from '@Queries/genericQueries';
import { useLocation } from 'react-router-dom';
import useListQuery from '@Hooks/useListQuery';
import { computeListSelector } from '@Organisms/User/selector';
import { UserOutput } from 'thehive-sdk';

/**
 * List all users from a specific organisation
 */
export const useListAllUsers = (): UseQueryResult<UserOutput[], QueryKey> => {
    const { data: currentUser } = useCurrentUser();
    const { organisation = '' } = currentUser || {};

    return useListQuery({
        controller: client.UserController,
        queryParams: {
            selector: computeListSelector({ orgId: organisation }),
        },
        fetchOnStart: false,
    });
};

/**
 * Returns the user currently connected
 */
export const useCurrentUser = (): UseQueryResult<UserOutput, QueryKey> => {
    const { search } = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const orgName = params.get('organisation') || '';

    const fetchCallback = useCallback(
        () =>
            client.UserController.getCurrent(orgName).then((user) => {
                params.delete('organisation');
                return user;
            }),
        [orgName, params],
    );

    return useGenericQuery({ fetchCallback, key: ['currentUser'], retry: false, manual: true });
};
